/**
 * Token display component
 * Shows current tokens with copy/clear functionality
 */

import { BaseComponent } from '../base-component';
import { AuthStore } from '../../state/auth-store';
import { APIClient } from '../../api';

export class TokenDisplay extends BaseComponent {
  private authStore: AuthStore;
  private apiClient: APIClient;

  constructor(container: HTMLElement, authStore: AuthStore, apiClient: APIClient) {
    super(container);
    this.authStore = authStore;
    this.apiClient = apiClient;
  }

  render(): void {
    this.updateDisplay();

    // Subscribe to auth changes
    this.subscriptions.push(
      this.authStore.subscribe(() => {
        this.updateDisplay();
      })
    );
  }

  private updateDisplay(): void {
    const tokens = this.authStore.getTokens();

    this.container.innerHTML = `
      <div class="token-display">
        <h4>Current Tokens (stored in localStorage)</h4>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
          <div>
            <small>ID Token:</small>
            <div class="token" id="current-id-token">${tokens?.idToken ? this.truncateToken(tokens.idToken) : '<span class="empty">Not logged in</span>'
      }</div>
          </div>
          <div>
            <small>Refresh Token:</small>
            <div class="token" id="current-refresh-token">${tokens?.refreshToken
        ? this.truncateToken(tokens.refreshToken)
        : '<span class="empty">Not logged in</span>'
      }</div>
          </div>
        </div>
        <div style="margin-top: 10px;">
          <button class="btn btn-secondary" id="clear-tokens-btn">Clear Tokens</button>
          <button class="btn btn-primary" style="margin-left: 8px;" id="copy-tokens-btn">Copy Tokens</button>
          <button class="btn btn-primary" style="margin-left: 8px;" id="fetch-tier-btn">Fetch Tier</button>
        </div>
        <div id="tier-display" style="margin-top: 10px; font-family: monospace; white-space: pre-wrap;"></div>
      </div>
    `;

    this.setupListeners();
  }

  private setupListeners(): void {
    const clearBtn = document.getElementById('clear-tokens-btn');
    const copyBtn = document.getElementById('copy-tokens-btn');
    const fetchTierBtn = document.getElementById('fetch-tier-btn');

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        this.authStore.clearTokens();
      });
    }

    if (copyBtn) {
      copyBtn.addEventListener('click', async () => {
        const tokens = this.authStore.getTokens();
        try {
          await navigator.clipboard.writeText(JSON.stringify(tokens, null, 2));
          alert('Tokens copied to clipboard');
        } catch (error) {
          console.error('Failed to copy tokens:', error);
          alert('Failed to copy tokens');
        }
      });
    }

    if (fetchTierBtn) {
      fetchTierBtn.addEventListener('click', async () => {
        const display = document.getElementById('tier-display');
        if (!display) return;

        display.textContent = 'Fetching tier...';
        try {
          // Temporarily set token in API Client from store if not set (for safety)
          const token = this.authStore.getIdToken();
          if (token) this.apiClient.setToken(token);

          const result = await this.apiClient.getTier();
          display.textContent = JSON.stringify(result, null, 2);
          display.style.color = result.tier === 'pro' ? '#4caf50' : '#ffa000'; // Green for pro, amber for free
        } catch (error) {
          console.error('Failed to fetch tier:', error);
          display.textContent = 'Error fetching tier: ' + (error instanceof Error ? error.message : String(error));
          display.style.color = 'red';
        }
      });
    }
  }

  private truncateToken(token: string): string {
    if (token.length > 50) {
      return token.substring(0, 25) + '...' + token.substring(token.length - 25);
    }
    return token;
  }
}
